/**
 * The `Display columns` pane (spec M1-B4 §8.7, `[ui:library/table-12-hide-columns.png]`).
 *
 * Measured x 875 -> 1215, y 237 -> 590: a search field `Find columns to show/hide`, a master
 * checkbox `All columns  <n> selected`, then a section `Item columns` with one checkbox per
 * column carrying its type icon and title. The `name` column cannot be hidden `[B5§9 L10]`.
 */
import { Icon, icons } from '@elizmonday/ui';
import { useState } from 'react';
import { getColumnUi } from '../../columns/registry';
import { useTable, useTableActions } from '../context';
import { NAME_COLUMN_ID } from '../store';
import { Pane, PANE_BOX } from './Pane';
import { useSaveLabel } from './useSaveLabel';

export function HidePane() {
  const payload = useTable((state) => state.payload);
  const effective = useTable((state) => state.effective);
  const dirty = useTable((state) => state.dirty);
  const actions = useTableActions();
  const saveLabel = useSaveLabel();
  const [query, setQuery] = useState('');

  const columns = (payload?.columns ?? []).filter((column) =>
    column.title.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const hidden = new Set(effective.hiddenColumnIds.map(String));
  const shown = (payload?.columns ?? []).filter((column) => !hidden.has(column.id)).length;
  const allShown = hidden.size === 0;

  return (
    <Pane
      testId="hide-pane"
      title="Display columns"
      {...PANE_BOX.hide}
      dirty={dirty}
      canSave={payload?.permissions.canSaveView ?? false}
      saveLabel={saveLabel}
      onSave={() => void actions.saveView()}
      onClose={() => actions.openPane(null)}
    >
      <input
        className="ez-board-pane__search"
        type="search"
        aria-label="Find columns to show/hide"
        placeholder="Find columns to show/hide"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      <label className="ez-hide__row ez-hide__row--all">
        <input
          type="checkbox"
          checked={allShown}
          aria-label="All columns"
          onChange={(event) =>
            actions.setEffective({
              hiddenColumnIds: event.target.checked
                ? []
                : (payload?.columns ?? [])
                    .filter((column) => column.id !== NAME_COLUMN_ID)
                    .map((column) => column.id),
            })
          }
        />
        <span>All columns</span>
        <span className="ez-hide__count">{`${shown} selected`}</span>
      </label>

      <h3 className="ez-hide__section">Item columns</h3>
      <ul className="ez-hide__list">
        {columns.map((column) => {
          const ui = getColumnUi(column.type);
          const visible = !hidden.has(column.id);
          return (
            <li key={column.id}>
              <label className="ez-hide__row">
                <input
                  type="checkbox"
                  checked={visible}
                  disabled={column.id === NAME_COLUMN_ID}
                  aria-label={column.title}
                  data-testid={`hide-${column.id}`}
                  onChange={(event) => actions.hideColumn(column.id, !event.target.checked)}
                />
                <Icon icon={icons[ui.icon]} size={16} ignoreFocusStyle />
                <span>{column.title}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </Pane>
  );
}
